/**
 * DEMO_NICHES_SHORT — landing-page card-shaped subset.
 *
 * Snapshot of 3 prod niches whose backing rows are locked from deletion via
 * migration 078. Card data is mirrored here so landing rendering does not
 * depend on a live DB read; click-through goes via a next.config.ts rewrite
 * that internally serves the prod page at /niche/{session_id}/c/{niche_id}
 * while the URL bar stays at /demo/niche/{slug}.
 *
 * Backing prod rows (snapshot 2026-05-06):
 *   creator-deal-desk → niches.id wtFIP1lKonLDApOuxo_bj, sessions.id RGHWKSdg0j5DR3bisa5jK
 *   str-margin-watch  → niches.id ph21WdLdgAxSGo8c37WQm, sessions.id E7dq5n3a7LBOKot2N7Fos
 *   mealplan-proof    → niches.id mUjye-TGKKJdP5dmXYKFn, sessions.id mZcpr2r6wEmhYbCj2gjCU
 *
 * Fields are sourced from prod (no fabrication):
 *   dreamHook       ← niches.short_description (2nd-person sensory line)
 *   dreamTranslation← niches.card_promise (outcome promise the user sees on the niche page)
 *   grade           ← getDebateGrade(niches.final_score) — recalibrated 2026-05-06
 *   visualGradient  ← niches.visual_gradient (mood + accent only — descriptor lives on full prod read)
 *   heroStat.revenueValue ← upper bound of niches.path_forward.monthlyRevenue.month12
 *   heroStat.firstSaleDay ← day extracted from niches.path_forward.nineDayHook
 *   effectiveScore  ← niches.final_score
 */

import type {
  DemoNicheAccentToken,
  DemoNicheGrade,
  DemoNicheVisualMood,
  DemoSessionMeta,
} from "./types";

// =====================================================================
// 1. DemoNicheShort type — card-shaped subset
// =====================================================================

export interface DemoNicheShort {
  /** kebab-case slug — landing card click flow goes to /demo/niche/{id}, which
   * is rewritten via next.config to /niche/{sessionId}/c/{nicheId}. */
  id: string;
  /** Display title — matches niches.name. */
  name: string;
  /** 2nd-person sensory hook — sourced from niches.short_description. */
  dreamHook: string;
  /** Outcome promise — sourced from niches.card_promise. Rendered as italic
   * caption under the revenue stat. */
  dreamTranslation: string;
  /** Letter grade — getDebateGrade(final_score). */
  grade: DemoNicheGrade;
  /** Hero color signal — mood + accent from niches.visual_gradient. */
  visualGradient: {
    mood: DemoNicheVisualMood;
    accent: DemoNicheAccentToken;
  };
  /** Hero stat — revenue + first-sale-day badges. */
  heroStat: {
    revenueValue: string;
    firstSaleDay: number;
  };
  /** Pre-computed top-pick score — equals niches.final_score. Lets Scene 5
   * `getTopPickShort()` rank without pulling extra fields. */
  effectiveScore: number;
}

// =====================================================================
// 2. Card-shaped literals — one per locked prod niche
// =====================================================================

const CREATOR_DEAL_DESK_SHORT: DemoNicheShort = {
  id: "creator-deal-desk",
  name: "Creator Deal Desk",
  dreamHook:
    "Tuesday 8:12am. You open Stripe at the kitchen counter. A creator pays $149 because a skincare brand asked for rates, and her old media kit is still a Canva file from last year.",
  dreamTranslation: "Close sponsor deals without agency fog.",
  grade: "S",
  visualGradient: {
    mood: "hot",
    accent: "warm-amber",
  },
  heroStat: {
    revenueValue: "$85k/mo",
    firstSaleDay: 9,
  },
  effectiveScore: 66,
};

const STR_MARGIN_WATCH_SHORT: DemoNicheShort = {
  id: "str-margin-watch",
  name: "STR Margin Watch",
  dreamHook:
    "Tuesday 8:14am. You open Stripe at the kitchen counter. A host pays $297 after your report catches 3 summer weekends priced $180 below the cabins across the lake.",
  dreamTranslation: "Protect $2K booking gaps before hosts underprice.",
  grade: "A+",
  visualGradient: {
    mood: "hot",
    accent: "warm-amber",
  },
  heroStat: {
    revenueValue: "$85k/mo",
    firstSaleDay: 9,
  },
  effectiveScore: 64,
};

const MEALPLAN_PROOF_SHORT: DemoNicheShort = {
  id: "mealplan-proof",
  name: "MealPlan Proof",
  dreamHook:
    "Wednesday 10:04pm. You sit at the kitchen counter and open email. A dietitian pays $129 after sending one messy Google Form and getting a client-ready plan back.",
  dreamTranslation: "Turn messy client food notes into usable plans.",
  grade: "A",
  visualGradient: {
    mood: "stable",
    accent: "deep-violet",
  },
  heroStat: {
    revenueValue: "$80k/mo",
    firstSaleDay: 11,
  },
  effectiveScore: 63,
};

// =====================================================================
// 3. Exports
// =====================================================================

export const DEMO_NICHES_SHORT: readonly DemoNicheShort[] = [
  CREATOR_DEAL_DESK_SHORT,
  STR_MARGIN_WATCH_SHORT,
  MEALPLAN_PROOF_SHORT,
];

export const SESSION_META: DemoSessionMeta = {
  nichesExplored: 10847,
  nichesKilledInFilter: 10844,
  nichesSurvived: 3,
  searchConfidence: 8,
};

/**
 * Highest effectiveScore niche — card-shape only. Used by landing Scene 5
 * Decide treatment.
 */
export function getTopPickShort(): DemoNicheShort {
  return DEMO_NICHES_SHORT.reduce(
    (top, curr) => (curr.effectiveScore > top.effectiveScore ? curr : top),
    DEMO_NICHES_SHORT[0],
  );
}
